import { Component, EventEmitter, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MemberService } from 'src/app/clubs/services/member-service';
import { GetClubsUnseenPublicationsResponse } from 'src/app/models/clubs/get-clubs-unseen-publications-response.model';
import { Publication } from 'src/app/models/clubs/publication.model';
import { ClubsService } from 'src/app/services/clubs/clubs.service';
import { MemberApiService } from 'src/app/services/clubs/member.api.service';

@Component({
    selector: 'app-drawer-notification',
    templateUrl: './drawer-notification.component.html',
    styleUrls: ['drawer-notification.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class DrawerNotificationComponent implements OnInit, OnDestroy {
    private destroy$: Subject<void> = new Subject<void>();

    public sidebarVisible: boolean = false;
    public dateNow: Date = new Date();

    public isLoadingPublications: boolean = false;
    public isDrawerInitialized: boolean = false;

    public publications: any[] = [];
    public publicationClubAndRoom = new Map<string, [string, string]>();

    public focusedPublication: Publication;
    public focusedPublicationDrawer = false;
    public initOnCommentField = false;

    public currentSort = { field: 'date', order: 'desc' }; // default

    @Output() notificationsCount = new EventEmitter<number>();

    constructor(
        private readonly router: Router,
        private readonly memberApiService: MemberApiService,
        private readonly memberService: MemberService,
        private readonly clubService: ClubsService,
        private translate: TranslateService,
    ) { }

    ngOnInit(): void {
        this.loadNotificationCount();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Load only the notification count for the badge
     */
    private loadNotificationCount(): void {
        this.memberApiService.getUnseenPublications()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (result: GetClubsUnseenPublicationsResponse) => {
                    this.notificationsCount.emit(result.publications.length);
                },
                error: (error) => {
                    this.notificationsCount.emit(0);
                }
            });
    }

    private loadPublicationsData(): void {
        if (this.isLoadingPublications || this.isDrawerInitialized) {
            return;
        }

        this.isLoadingPublications = true;

        this.memberApiService.getUnseenPublications()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (result: GetClubsUnseenPublicationsResponse) => {
                    this.publications = result.publications;

                    // Load club and room titles for each publication
                    this.publications.forEach((pub) => {
                        this.populateClubAndRoomTitles(pub.id, pub.club);
                    });

                    this.notificationsCount.emit(result.publications.length);
                    this.isDrawerInitialized = true;
                    this.isLoadingPublications = false;
                },
                error: (error) => {
                    this.isLoadingPublications = false;
                    this.isDrawerInitialized = true; // Mark as initialized to avoid repeated calls
                }
            });
    }

    public openDrawer(): void {
        this.sidebarVisible = true;

        if (!this.isDrawerInitialized) {
            this.loadPublicationsData();
        }
    }

    public closeDrawer(): void {
        this.sidebarVisible = false;
    }

    public populateClubAndRoomTitles(pubId: string, club: any) {
        if (!club.isAParentClub && club.isARoom) {
            this.clubService.getClubById(club.parentClubId)
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                    next: (parentClub: any) => {
                        this.publicationClubAndRoom.set(pubId, [parentClub.title, club.title]); // if is a room, look for the parent club title
                    }
                });
        } else {
            this.publicationClubAndRoom.set(pubId, [club.title, this.translate.instant('clubs.default.defaultRoomName')]); // if no parent club, room == general
        }
    }

    public getClubAndRoom(pubId: string): [string, string] {
        return this.publicationClubAndRoom.get(pubId) || ['', ''];
    }

    public getPublicationTypeTranslation(type: string): string {
        switch (type) {
            case 'announcement':
                return 'a annoncé';
            case 'comment':
                return 'a commenté';
            case 'publication':
                return 'a publié';
            default:
                return 'a publié';
        }
    }

    public sort(field: 'date' | 'clubName', order: 'asc' | 'desc') {
        this.currentSort = { field, order };

        this.publications.sort((a, b) => {
            let valueA: number | string;
            let valueB: number | string;

            if (field === 'date') {
                valueA = new Date(a.creationDate).getTime();
                valueB = new Date(b.creationDate).getTime();
            } else if (field === 'clubName') {
                const clubInfoA = this.publicationClubAndRoom.get(a.clubId);
                const clubInfoB = this.publicationClubAndRoom.get(b.clubId);
                valueA = (clubInfoA?.[0] || '').toLowerCase();
                valueB = (clubInfoB?.[0] || '').toLowerCase();
            }

            if (valueA < valueB) return order === 'asc' ? -1 : 1;
            if (valueA > valueB) return order === 'asc' ? 1 : -1;
            return 0;
        });
    }

    public onSeeMore(event: [Publication, boolean]) {
        this.focusedPublicationDrawer = true
        this.focusedPublication = event[0]; // received publication obj to show
        this.initOnCommentField = event[1]; // received whether to focus on comment field or not
    }

    public closeFocusedPublicationDrawer(): void {
        this.focusedPublicationDrawer = false;
        this.focusedPublication = undefined;
    }

    public goToPublicationPage(clubId: string, publicationId: string): void {
        this.router.navigate(['clubs', clubId, 'post', publicationId]);
    }

    public readAll(): void {
        this.memberService.updateAllClubsLastVisitDate();
        this.publications = [];
        this.publicationClubAndRoom.clear();
        this.notificationsCount.emit(0);
        this.isDrawerInitialized = false;
    }
}
